// src/pages/CategoryCartoonPage.js
import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { smartExcerpt, formatDate } from "../utils/PageParser";
import SEO from "../components/SEO";

import { fetchContent } from "../content/content";
import { fetchMedia } from "../content/media";


const BASE_URL = import.meta?.env?.VITE_SITE_URL || "https://werlocal.us";


/* ──────────────────────────────────────────────────────────────────────────── */
/* Category metadata (trimmed to 'news' for this page)                         */
export const CATEGORY_SEO = {
  news: {
    title: "News — We Are Local",
    description:
      "News for people who want the key facts in a positive, simple way — without getting dragged into politics.",
    keywords: "news, main news for day, We Are Local",
  },
};

const CATEGORY_BLURB = {
  news: "One News a Day: Just the key facts — positive, simple, and easy to follow, without the politics",
};

const CATEGORY_OG = {
  news: "https://werlocalbucket.s3.us-west-2.amazonaws.com/news.jpg",
};


function normalizeExcerpt(str = "", max = 200) {
  const clean = stripHtml(str).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;

  const cut = clean.slice(0, max);
  const lastPeriod = cut.lastIndexOf(". ");
  if (lastPeriod > max * 0.5) return cut.slice(0, lastPeriod + 1);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + "…";
}
/* ──────────────────────────────────────────────────────────────────────────── */
/* Text normalizers: strip HTML, collapse whitespace, smart clamp              */


function stripHtml(str = "") {
  return String(str).replace(/<[^>]+>/g, " ");
}

function normalizeText(str = "", max = 220) {
  const clean = stripHtml(str).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const soft = clean.slice(0, max);
  const sentenceCut = soft.lastIndexOf(". ");
  if (sentenceCut > max * 0.5) return soft.slice(0, sentenceCut + 1);
  const wordCut = soft.lastIndexOf(" ");
  return (wordCut > 0 ? soft.slice(0, wordCut) : soft) + "…";
}

/* ──────────────────────────────────────────────────────────────────────────── */
/* Two-column row: image left, text right                                      */
// replace ONLY the ArticleRow in CategoryCartoonPage.js

function pretty(str = "") {
  return String(str)
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (m) => m.toUpperCase());
}

// --- replace ONLY your ArticleRow with this ---
function ArticleRow({ a, categoryId }) {
  const thumb =
    (a.image && a.image.trim?.()) ||
    (a.ogImage && a.ogImage.trim?.()) ||
    CATEGORY_OG?.[categoryId] ||
    `${BASE_URL}/og/default.png`;

  const rawTitle = a.title ?? a.t ?? a.name ?? a.h ?? "";
  const title = normalizeText(rawTitle, 120);

  const rawDesc = a.description ?? a.excerpt ?? a.page ?? "";
  const excerpt = normalizeText(rawDesc, 220);

  // topic pill text
  const topicId   = String(a.topic || a.type || categoryId || "").toLowerCase();
  const topicText = topicId
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^\w/, (m) => m.toUpperCase());

  return (
    <div className="row-card mb-3 d-flex">
      {/* LEFT: thumbnail */}
      <Link to={`/article/${a.id}`} className="row-card_thumb me-3" aria-label={title}>
        <img src={thumb} alt={title || "Article"} loading="lazy" width="120" height="86" />
      </Link>

      {/* RIGHT: text */}
      <div className="row-card_body flex-grow-1">
        {/* Headline */}
        {title && (
          <h3 className="row-card__title fw-bold">
            <Link to={`/article/${a.id}`} className="hl-link">{title}</Link>
          </h3>
        )}

        {/* Topic pill + date */}
        <div className="row-card__meta d-flex align-items-center gap-2 mb-1">
          {topicId && (
            <Link
              to={`/article/${a.id}`}
              className="topic-pill"
            >
              {topicText}
            </Link>
            
          )}
          {a.d && <span className="hl-hl-link">{formatDate(a.d)}</span>}
        </div>

        {/* Excerpt */}
        {excerpt && <div className="hl-excerpt">{excerpt}</div>}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────── */

export default function CategoryCartoonPage() {
  const { categoryId: paramCategoryId } = useParams();
  const categoryId = (paramCategoryId || "").toLowerCase();

  const dispatch = useDispatch();

  // Articles
  const items = useSelector((s) => s.content?.byCategory?.[categoryId] || []);
  const contentStatus = useSelector((s) => s.content?.status);
  const contentError = useSelector((s) => s.content?.error);

  // Videos
  const mediaItems = useSelector((s) => s.media?.byCategory?.[categoryId] || []);
  const mediaStatus = useSelector((s) => s.media?.status);
  const mediaError = useSelector((s) => s.media?.error);

  useEffect(() => {
    if (categoryId && typeof fetchContent === "function") {
      dispatch(fetchContent({ type: categoryId, tags: "" }));
    }
    if (categoryId && typeof fetchMedia === "function") {
      dispatch(fetchMedia({ type: categoryId, tags: "" }));
    }
  }, [dispatch, categoryId]);

  // Filter to category by type/topic/tags
  const filteredArticles = useMemo(() => {
    const c = (categoryId || "").toLowerCase();
    return (items || []).filter((o) => {
      const cat = (o.type || o.topic || "").toLowerCase();
      const tags = String(o.tags || "").toLowerCase();
      return cat === c || tags.includes(c);
    });
  }, [items, categoryId]);

  // Sort newest first (optional; remove if you already sort server-side)
  filteredArticles.sort((a, b) => {
    const ad = a.d ? new Date(a.d).getTime() : 0;
    const bd = b.d ? new Date(b.d).getTime() : 0;
    return bd - ad;
  });

  // Split: top/pinned + the rest
  const top = filteredArticles.slice(0, 5);
  const otherNews = filteredArticles.slice(5, 30);

  // Videos list
  const videoItems = useMemo(() => {
    return (mediaItems || [])
      .filter((v) => typeof v.video === "string" && v.video.trim())
      .sort((a, b) => {
        const av = a.orderby ?? 0;
        const bv = b.orderby ?? 0;
        if (av !== bv) return av - bv;
        const ad = a.d ? new Date(a.d).getTime() : 0;
        const bd = b.d ? new Date(b.d).getTime() : 0;
        return bd - ad;
      });
  }, [mediaItems]);

  // SEO
  const seo = CATEGORY_SEO[categoryId] || {
    title: `${normalizeText(categoryId || "News", 60)} — We Are Local`,
    description: "Local news, community stories, and highlights.",
    keywords: "local news, community, We Are Local",
  };
  const prettyCategory = normalizeText(
    (categoryId || "").replace(/-/g, " ").replace(/^\w/, (m) => m.toUpperCase()),
    60
  );
  const baseDesc = CATEGORY_BLURB[categoryId] || "Latest updates and stories from your community.";
  const ogImage = CATEGORY_OG[categoryId] || `${BASE_URL}/og/default.png`;
  const canonical = `${BASE_URL}/categoryimage/${encodeURIComponent(categoryId)}`;

  if (contentStatus === "loading" && filteredArticles.length === 0) {
    return <div className="container mt-4">Loading…</div>;
  }
  if (contentError) {
    return <div className="container mt-4 text-danger">Error: {String(contentError)}</div>;
  }

  return (
    <div className="container-fluid py-3">
      {/* SEO */}
      <SEO
        title={seo.title}
        description={seo.description}
        robots="index, follow"
        canonical={canonical}
        og={{ title: seo.title, description: seo.description, type: "website", image: ogImage }}
        twitter={{ card: "summary_large_image", title: seo.title, description: seo.description, image: ogImage }}
      />

      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-2">
        {CATEGORY_OG[categoryId] && (
          <img
            src={CATEGORY_OG[categoryId]}
            alt={`${prettyCategory} category`}
            className="category-chip"
            loading="lazy"
            width="64"
            height="64"
          />
        )}
        <div>
          <h1 className="mb-2 text-capitalize">{prettyCategory}</h1>
          <p className="site-tagline">{baseDesc}</p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="row g-4">
        {/* LEFT: pinned + list */}
        <div className="col-lg-9">
          {/* Pinned */}
          <div className="headline-list mb-3">
            {top.map((a) => (
              <ArticleRow key={a.id} a={a} categoryId={categoryId} />
            ))}
          </div>

          {/* Articles */}
          <div className="articles-list">
            {otherNews.map((a) => (
              <ArticleRow key={a.id} a={a} categoryId={categoryId} />
            ))}
          </div>
        </div>

        

        {/* RIGHT: videos / ads */}
        <div className="col-lg-3">
          <div className="section-title mb-2">Interesting</div>

          {mediaStatus === "loading" && <div className="text-muted mb-2">Loading videos…</div>}
          {mediaError && <div className="text-danger mb-2">Video error</div>}

          {videoItems.slice(0, 3).map((v) => (
            <div
              key={v.id}
              className="ratio ratio-16x9 mb-3"
              dangerouslySetInnerHTML={{ __html: v.video }}
            />
          ))}

          {/* keep your ad/promos below here if needed */}
        </div>
      </div>
    </div>
  );
}